/** @jsxImportSource @emotion/react */
import React from 'react';
import styled from '@emotion/styled';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { AnalyticsMatchRecord } from '../../domain/interfaces/tournament-analytics';

interface MatchRecordCardProps {
  title: string;
  icon: React.ReactNode;
  record: AnalyticsMatchRecord | null;
  highlightLabel: string;
}

const Card = styled.div`
  background: ${({ theme }) => theme.colors.SurfaceWhite};
  border: 1px solid ${({ theme }) => theme.colors.SurfacePlatinum20};
  border-left: 4px solid ${({ theme }) => theme.colors.DarkYellow};
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.DarkBlue};
  font-size: 14px;
  font-weight: 600;

  svg {
    color: ${({ theme }) => theme.colors.DarkYellow};
    flex-shrink: 0;
  }
`;

const EmptyState = styled.div`
  color: ${({ theme }) => theme.colors.Gray};
  font-size: 14px;
  text-align: center;
  padding: 12px 0;
`;

const ScoreRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const TeamName = styled.span`
  color: ${({ theme }) => theme.colors.DarkBlue};
  font-size: 15px;
  font-weight: 600;
  flex: 1;
  text-align: center;
`;

const Score = styled.span`
  color: ${({ theme }) => theme.colors.DarkYellow};
  font-size: 22px;
  font-weight: 700;
  white-space: nowrap;
`;

const Footer = styled.div`
  color: ${({ theme }) => theme.colors.WhiteGray};
  font-size: 12px;
  text-align: center;
`;

const MatchRecordCard = React.memo(({ title, icon, record, highlightLabel }: MatchRecordCardProps) => {
  return (
    <Card>
      <TitleRow>
        {icon}
        <span>{title}</span>
      </TitleRow>
      {record === null ? (
        <EmptyState>Нет данных за выбранный период</EmptyState>
      ) : (
        <>
          <ScoreRow>
            <TeamName>{record.teamAName}</TeamName>
            <Score>
              {record.teamAScore} : {record.teamBScore}
            </Score>
            <TeamName>{record.teamBName}</TeamName>
          </ScoreRow>
          <Footer>
            {record.championshipName} · {format(new Date(record.matchDate), 'd MMM yyyy', { locale: ru })} · {highlightLabel}: {record.highlightValue}
          </Footer>
        </>
      )}
    </Card>
  );
});

export default MatchRecordCard;
